import { Modal, Tabs } from 'antd'
import { useEffect, useState } from 'react'

import LockIcon from '@Icons/lock-01.svg?react'

import FeedUrlSourceAuthenticationApiKey from './FeedAuthenticationApiKey'
import FeedUrlSourceAuthenticationBasic from './FeedAuthenticationBasic'

interface FeedUrlSourceAuthenticationProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  onSubmit: (
    type: 'basic' | 'api_key' | null,
    credentials:
      | { username: string; password: string }
      | { api_key: string }
      | null,
  ) => void
  initialValues?: {
    authType: 'BASIC' | 'API_KEY' | null
    authPayload:
      | { username: string; password: string }
      | { api_key: string }
      | null
  }
}

function FeedUrlSourceAuthentication({
  isOpen,
  setIsOpen,
  onSubmit,
  initialValues,
}: FeedUrlSourceAuthenticationProps): JSX.Element {
  const [submitParams, setSubmitParams] = useState<
    | ['basic', { username: string; password: string }]
    | ['api_key', { api_key: string }]
    | [null, null]
    | null
  >(null)

  useEffect(() => {
    if (!submitParams) {
      return
    }

    const t = setTimeout(() => {
      const [type, credentials] = submitParams
      onSubmit(type, credentials)

      setSubmitParams(null)
    }, 300)

    return () => clearTimeout(t)
  }, [submitParams, onSubmit])

  return (
    <Modal
      title={
        <div className="flex justify-center mb-5">
          <LockIcon />
        </div>
      }
      centered
      keyboard={false}
      mask={{
        blur: true,
        enabled: true,
        closable: false,
      }}
      open={isOpen}
      footer={null}
      onCancel={() => setIsOpen(false)}
    >
      <div className="mb-6">
        <h1 className="text-center text-lg text-gray-900 font-medium mb-1">
          Add Authentication Information
        </h1>
        <p className="text-gray-500 text-sm">
          Please enter your feed source's credentials to allow Prisync to access
          it.
        </p>
      </div>
      <Tabs
        defaultActiveKey="1"
        className="[&>.ant-tabs-nav>.ant-tabs-nav-wrap>.ant-tabs-nav-list>.ant-tabs-ink-bar]:!bg-primary-600 [&>.ant-tabs-content-holder]:!pt-4"
        size="large"
        classNames={{
          item: '[&>.ant-tabs-tab-btn]:text-gray-700 [&.ant-tabs-tab-active>.ant-tabs-tab-btn]:!text-primary-600',
        }}
        items={[
          {
            key: '1',
            label: 'Username & Password',
            children: (
              <FeedUrlSourceAuthenticationBasic
                isSubmitted={submitParams?.[0] === 'basic'}
                onSubmit={(credentials) =>
                  setSubmitParams(
                    credentials ? ['basic', credentials] : [null, null],
                  )
                }
                initialValues={
                  initialValues?.authType === 'BASIC'
                    ? (initialValues.authPayload as {
                        username: string
                        password: string
                      })
                    : undefined
                }
              />
            ),
          },
          {
            key: '2',
            label: 'API Key',
            children: (
              <FeedUrlSourceAuthenticationApiKey
                isSubmitted={submitParams?.[0] === 'api_key'}
                onSubmit={(credentials) =>
                  setSubmitParams(
                    credentials ? ['api_key', credentials] : [null, null],
                  )
                }
                initialValues={
                  initialValues?.authType === 'API_KEY'
                    ? (initialValues.authPayload as { api_key: string })
                    : undefined
                }
              />
            ),
          },
        ]}
      />
    </Modal>
  )
}

export default FeedUrlSourceAuthentication
